import React, { useState, useEffect } from 'react';
import { 
  Users, Plus, X, Search, Shield, Settings, UserMinus, UserPlus, 
  Crown, AlertTriangle, Edit3, Trash2, CheckCircle, Building2 
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useChat } from '../../contexts/ChatContext';
import { ProjectChatService } from '../../services/projectChatService';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';

interface ProjectMember {
  id: string;
  name: string;
  role: string;
  avatar?: string;
  email?: string;
  phone?: string;
  department?: string;
  joinDate?: string;
  auth_id?: string;
  user_id?: number;
}

interface AvailableMember {
  id: number;
  name: string;
  email?: string;
  avatar?: string;
  auth_id?: string;
}

interface ProjectMembersManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

const ProjectMembersManager: React.FC<ProjectMembersManagerProps> = ({
  isOpen,
  onClose
}) => {
  const { organization } = useOrganization();
  const { currentRoom, isCurrentUserAdmin } = useChat();
  const [activeTab, setActiveTab] = useState<'members' | 'add'>('members');
  const [searchTerm, setSearchTerm] = useState('');
  const [projectMembers, setProjectMembers] = useState<ProjectMember[]>([]);
  const [availableMembers, setAvailableMembers] = useState<AvailableMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedMember, setSelectedMember] = useState<ProjectMember | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const toast = useToast();
  const { confirm } = useConfirm();

  // Verificar se o usuário atual é administrador da sala
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        // Para projetos, vamos permitir que qualquer usuário membro da organização gerencie membros
        // Isso pode ser refinado depois baseado em permissões específicas do projeto
        if (organization?.id && currentRoom?.project_id) {
          setIsAdmin(true);
          console.log('ProjectMembersManager: Usuário tem permissão para gerenciar membros do projeto');
        } else {
          setIsAdmin(false);
        }
      } catch (error) {
        console.error('Erro ao verificar status de admin:', error);
        setIsAdmin(false);
      }
    };

    if (currentRoom && isOpen) {
      checkAdminStatus();
    }
  }, [currentRoom, organization?.id, isOpen]);

  // Buscar membros do projeto
  useEffect(() => {
    const fetchProjectMembers = async () => {
      if (!organization?.id || !currentRoom?.project_id || !isOpen) return;

      try {
        setIsLoading(true);
        console.log('Buscando membros do projeto:', currentRoom.project_id);
        
        // Buscar membros ativos do projeto
        const { data: projectTeamMembers, error: projectTeamError } = await supabase
          .from('project_team_members')
          .select(`
            id,
            name,
            role,
            status,
            auth_id,
            created_at,
            start_date
          `)
          .eq('project_id', currentRoom.project_id)
          .eq('status', 'active');
        
        if (projectTeamError) {
          console.error('Erro ao buscar membros do projeto:', projectTeamError);
          throw projectTeamError;
        }
        
        console.log('Membros encontrados em project_team_members:', projectTeamMembers);
        
        if (!projectTeamMembers || projectTeamMembers.length === 0) {
          setProjectMembers([]);
          setIsLoading(false);
          return;
        }
        
        const formattedMembers: ProjectMember[] = [];
        
        for (const member of projectTeamMembers) {
          // Verificar se temos auth_id válido
          const authId = member.auth_id;
          
          if (authId && authId.trim() !== '') {
            try {
              // Buscar informações do perfil do usuário
              const { data: userProfile, error: profileError } = await supabase
                .from('user_profile')
                .select('id, name, email, user_photo, whatsapp, bio, created_at')
                .eq('auth_id', authId)
                .single();
              
              if (!profileError && userProfile) {
                formattedMembers.push({
                  id: authId,
                  name: userProfile.name || member.name || 'Membro do Projeto',
                  email: userProfile.email || '',
                  role: member.role || 'Membro',
                  avatar: userProfile.user_photo || undefined,
                  phone: userProfile.whatsapp || '',
                  department: userProfile.bio || '',
                  joinDate: member.start_date || member.created_at || userProfile.created_at || '',
                  auth_id: authId,
                  user_id: userProfile.id
                });
              } else {
                // Usar dados do próprio membro se disponíveis
                if (member.name && member.name.trim() !== '') {
                  formattedMembers.push({
                    id: authId,
                    name: member.name,
                    role: member.role || 'Membro',
                    joinDate: member.start_date || member.created_at || '',
                    auth_id: authId
                  });
                }
              }
            } catch (profileError) {
              console.error('Erro ao buscar perfil do usuário:', profileError);
              // Adicionar mesmo com erro, usando dados disponíveis
              formattedMembers.push({
                id: authId,
                name: member.name || `Usuário ${authId.substring(0, 8)}`,
                role: member.role || 'Membro',
                joinDate: member.start_date || member.created_at || '',
                auth_id: authId
              });
            }
          } else if (member.name && member.name.trim() !== '') {
            // Membro sem auth_id válido mas com nome
            formattedMembers.push({
              id: member.id || `member-${Date.now()}`,
              name: member.name,
              role: member.role || 'Membro',
              joinDate: member.start_date || member.created_at || ''
            });
          }
        }
        
        console.log('Membros formatados finais:', formattedMembers);
        setProjectMembers(formattedMembers);
        setIsLoading(false);
      } catch (err) {
        console.error('Erro ao buscar membros:', err);
        setIsLoading(false);
      }
    };

    fetchProjectMembers();
  }, [organization?.id, currentRoom?.project_id, isOpen]);

  // Buscar membros disponíveis para adicionar
  useEffect(() => {
    const fetchAvailableMembers = async () => {
      if (!organization?.id || !isOpen) return;

      try {
        console.log('Buscando membros disponíveis para adicionar ao projeto...');
        setIsLoading(true);
        
        // Buscar membros da organização que não estão no projeto
        const { data: orgMembers, error: orgError } = await supabase
          .from('user_profile')
          .select('id, name, email, user_photo, auth_id')
          .eq('organization_id', organization.id)
          .eq('archived', false);

        if (orgError) {
          console.error('Erro ao buscar membros da organização:', orgError);
          throw orgError;
        }

        console.log('Membros da organização encontrados:', orgMembers?.length || 0);

        if (orgMembers && orgMembers.length > 0) {
          const formattedMembers: AvailableMember[] = [];
          
          for (const member of orgMembers) {
            // Verificar se já é membro do projeto
            const isAlreadyMember = projectMembers.find(m => 
              m.auth_id === member.auth_id || m.user_id === member.id
            );
            if (isAlreadyMember) {
              console.log(`Membro ${member.name} já está no projeto, pulando...`);
              continue;
            }
            
            formattedMembers.push({
              id: member.id,
              name: member.name || 'Usuário sem nome',
              email: member.email || undefined,
              avatar: member.user_photo || undefined,
              auth_id: member.auth_id
            });
          }
          
          console.log('Membros disponíveis para adicionar:', formattedMembers.length);
          setAvailableMembers(formattedMembers);
        } else {
          console.log('Nenhum membro da organização encontrado');
          setAvailableMembers([]);
        }
        
        setIsLoading(false);
      } catch (err) {
        console.error('Erro ao buscar membros:', err);
        setIsLoading(false);
        setAvailableMembers([]);
      }
    };

    // Execute sempre que o modal abrir ou quando os membros do projeto mudarem
    fetchAvailableMembers();
  }, [organization?.id, isOpen, projectMembers]);

  const filteredMembers = projectMembers.filter(member => 
    searchTerm === '' || 
    member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (member.email && member.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const filteredAvailableMembers = availableMembers.filter(member => 
    searchTerm === '' || 
    member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (member.email && member.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleAddMember = async (member: AvailableMember) => {
    if (!currentRoom?.project_id || !organization?.id) {
      console.error('Dados necessários não disponíveis:', {
        project_id: currentRoom?.project_id,
        organization_id: organization?.id
      });
      toast.error('Erro', 'Dados do projeto ou organização não disponíveis.');
      return;
    }

    try {
      setIsLoading(true);
      
      console.log('Tentando adicionar membro ao projeto:', {
        member: {
          id: member.id,
          name: member.name,
          auth_id: member.auth_id
        },
        project_id: currentRoom.project_id,
        organization_id: organization.id
      });
      
      // Dados para inserção
      const insertData = {
        project_id: currentRoom.project_id,
        organization_id: organization.id,
        name: member.name,
        role: 'Membro',
        status: 'active',
        start_date: new Date().toISOString().split('T')[0],
        allocation_percentage: 100,
        auth_id: member.auth_id
      };
      
      console.log('Dados para inserção:', insertData);
      
      // Adicionar membro ao projeto
      const { data: insertedData, error: insertError } = await supabase
        .from('project_team_members')
        .insert(insertData)
        .select();

      if (insertError) {
        console.error('Erro detalhado ao adicionar membro ao projeto:', {
          error: insertError,
          code: insertError.code,
          message: insertError.message,
          details: insertError.details,
          hint: insertError.hint
        });
        
        // Mostrar erro específico para o usuário
        let userMessage = 'Erro ao adicionar membro ao projeto';
        if (insertError.message.includes('permission')) {
          userMessage = 'Você não tem permissão para adicionar membros a este projeto';
        } else if (insertError.message.includes('duplicate')) {
          userMessage = 'Este membro já faz parte do projeto';
        } else if (insertError.message.includes('foreign key')) {
          userMessage = 'Dados inválidos. Verifique se o projeto e organização existem';
        }
        
        toast.error(userMessage, insertError.message);
        throw insertError;
      }

      console.log(`✅ Membro ${member.name} adicionado ao projeto com sucesso:`, insertedData);
      
      // Sincronizar automaticamente com o chat
      try {
        console.log('🔄 Sincronizando membro com o chat...');
        const { data: chatRoom } = await supabase
          .from('chat_rooms')
          .select('id')
          .eq('project_id', currentRoom.project_id)
          .single();
          
        if (chatRoom) {
          await ProjectChatService.syncProjectMembers(currentRoom.project_id, chatRoom.id);
          console.log('✅ Membro sincronizado com o chat com sucesso');
        } else {
          console.log('ℹ️ Nenhuma sala de chat encontrada para este projeto');
        }
      } catch (chatError) {
        console.error('⚠️ Erro ao sincronizar membro com o chat:', chatError);
      }
      
      toast.success(`Membro ${member.name} adicionado ao projeto com sucesso!`);
      
      // Recarregar membros do projeto imediatamente
      const { data: updatedMembers } = await supabase
        .from('project_team_members')
        .select(`
          id,
          name,
          role,
          status,
          auth_id,
          created_at,
          start_date
        `)
        .eq('project_id', currentRoom.project_id)
        .eq('status', 'active');
      
      if (updatedMembers) {
        console.log('Lista de membros atualizada:', updatedMembers);
        // Reprocessar membros
        const formattedMembers: ProjectMember[] = [];
        
        for (const member of updatedMembers) {
          const authId = member.auth_id;
          
          if (authId && authId.trim() !== '') {
            try {
              const { data: userProfile } = await supabase
                .from('user_profile')
                .select('id, name, email, user_photo, whatsapp, bio, created_at')
                .eq('auth_id', authId)
                .single();
              
              if (userProfile) {
                formattedMembers.push({
                  id: authId,
                  name: userProfile.name || member.name || 'Membro do Projeto',
                  email: userProfile.email || '',
                  role: member.role || 'Membro',
                  avatar: userProfile.user_photo || undefined,
                  phone: userProfile.whatsapp || '',
                  department: userProfile.bio || '',
                  joinDate: member.start_date || member.created_at || userProfile.created_at || '',
                  auth_id: authId,
                  user_id: userProfile.id
                });
              }
            } catch (error) {
              console.error('Erro ao buscar perfil:', error);
            }
          }
        }
        
        setProjectMembers(formattedMembers);
      }
      
      // Limpar busca e voltar para aba de membros
      setSearchTerm('');
      setActiveTab('members');
      setIsLoading(false);
      
    } catch (err) {
      console.error('Erro geral ao adicionar membro:', err);
      toast.error('Erro inesperado ao adicionar membro', 'Verifique o console para mais detalhes.');
      setIsLoading(false);
    }
  };

  const handleRemoveMember = async (member: ProjectMember) => {
    if (!currentRoom?.project_id || !member.auth_id) return;

    const ok = await confirm({
      message: `Tem certeza que deseja remover ${member.name} do projeto?`,
      variant: 'danger',
    });
    if (!ok) {
      return;
    }

    try {
      setIsLoading(true);
      
      // Remover membro do projeto (marcar como inativo)
      const { error: updateError } = await supabase
        .from('project_team_members')
        .update({ status: 'inactive' })
        .eq('project_id', currentRoom.project_id)
        .eq('auth_id', member.auth_id);

      if (updateError) {
        console.error('Erro ao remover membro do projeto:', updateError);
        throw updateError;
      }

      console.log(`Membro ${member.name} removido do projeto`);

      // Sincronizar com o chat
      try {
        const { data: chatRoom } = await supabase
          .from('chat_rooms')
          .select('id')
          .eq('project_id', currentRoom.project_id)
          .single();
          
        if (chatRoom) {
          await ProjectChatService.syncProjectMembers(currentRoom.project_id, chatRoom.id);
        }
      } catch (chatError) {
        console.error('Erro ao sincronizar remoção com o chat:', chatError);
      }

      // Atualizar lista local
      setProjectMembers(prev => prev.filter(m => m.id !== member.id));
      setIsLoading(false);
    } catch (err) {
      console.error('Erro ao remover membro:', err);
      setIsLoading(false);
    }
  };

  const handleRoleChange = async (member: ProjectMember, newRole: string) => {
    if (!currentRoom?.project_id || !member.auth_id) return;

    const ok = await confirm({
      message: `Tem certeza que deseja alterar o papel de ${member.name} para ${newRole}?`,
      variant: 'default',
    });
    if (!ok) {
      return;
    }

    try {
      setIsLoading(true);
      
      // Atualizar papel do membro no projeto
      const { error: updateError } = await supabase
        .from('project_team_members')
        .update({ role: newRole })
        .eq('project_id', currentRoom.project_id)
        .eq('auth_id', member.auth_id);

      if (updateError) {
        console.error('Erro ao atualizar papel do membro:', updateError);
        throw updateError;
      }

      console.log(`Papel do membro ${member.name} atualizado para ${newRole}`);

      // Atualizar lista local
      setProjectMembers(prev => prev.map(m => 
        m.id === member.id ? { ...m, role: newRole } : m
      ));
      
      setSelectedMember(null);
      setShowRoleModal(false);
      setIsLoading(false);
    } catch (err) {
      console.error('Erro ao atualizar papel do membro:', err);
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <Building2 className="w-6 h-6 text-coral" />
            <h2 className="text-xl font-semibold">Gerenciar Membros do Projeto</h2>
            {isAdmin && (
              <div title="Você é administrador desta sala">
                <Shield className="w-5 h-5 text-coral" />
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('members')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'members'
                ? 'text-coral border-b-2 border-coral'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Membros do Projeto ({projectMembers.length})
          </button>
          {isAdmin && (
            <button
              onClick={() => setActiveTab('add')}
              className={`px-6 py-3 font-medium ${
                activeTab === 'add'
                  ? 'text-coral border-b-2 border-coral'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Adicionar ao Projeto
            </button>
          )}
        </div>

        {/* Search */}
        <div className="p-6 border-b">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={activeTab === 'members' ? 'Buscar membros do projeto...' : 'Buscar pessoas para adicionar...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-coral"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'members' ? (
            <div className="space-y-3">
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
                </div>
              ) : filteredMembers.length === 0 ? (
                <p className="text-center py-8 text-gray-500">
                  {searchTerm ? 'Nenhum membro encontrado' : 'Nenhum membro no projeto'}
                </p>
              ) : (
                filteredMembers.map(member => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                        {member.avatar ? (
                          <img
                            src={member.avatar}
                            alt={member.name}
                            className="w-12 h-12 rounded-full"
                          />
                        ) : (
                          <span className="text-lg font-medium">
                            {member.name.charAt(0)}
                          </span>
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-lg">{member.name}</div>
                        <div className="text-sm text-gray-500">{member.role}</div>
                        {member.email && (
                          <div className="text-xs text-gray-400">{member.email}</div>
                        )}
                      </div>
                    </div>
                    
                    {isAdmin && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setSelectedMember(member);
                            setShowRoleModal(true);
                          }}
                          className="p-2 hover:bg-gray-200 rounded-full"
                          title="Alterar papel"
                        >
                          <Edit3 className="w-4 h-4 text-gray-600" />
                        </button>
                        <button
                          onClick={() => handleRemoveMember(member)}
                          className="p-2 hover:bg-gray-200 rounded-full"
                          title="Remover do projeto"
                        >
                          <Trash2 className="w-4 h-4 text-nk-error" />
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
                </div>
              ) : filteredAvailableMembers.length === 0 ? (
                <p className="text-center py-8 text-gray-500">
                  {searchTerm ? 'Nenhuma pessoa encontrada' : 'Todas as pessoas já estão no projeto'}
                </p>
              ) : (
                filteredAvailableMembers.map(member => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                        {member.avatar ? (
                          <img
                            src={member.avatar}
                            alt={member.name}
                            className="w-12 h-12 rounded-full"
                          />
                        ) : (
                          <span className="text-lg font-medium">
                            {member.name.charAt(0)}
                          </span>
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-lg">{member.name}</div>
                        <div className="text-sm text-gray-500">
                          {member.email || 'Membro da organização'}
                        </div>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => handleAddMember(member)}
                      className="btn-primary"
                      disabled={isLoading}
                    >
                      <UserPlus className="w-4 h-4" />
                      Adicionar ao Projeto
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Aviso para não administradores */}
        {!isAdmin && (
          <div className="p-6 border-t bg-yellow-50">
            <div className="flex items-center gap-2 text-nk-warn">
              <AlertTriangle className="w-5 h-5" />
              <span>
                Apenas administradores podem gerenciar membros do projeto.
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Modal para alterar papel do membro */}
      {showRoleModal && selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg p-6 w-96 max-w-90vw">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Alterar Papel no Projeto</h3>
              <button
                onClick={() => {
                  setShowRoleModal(false);
                  setSelectedMember(null);
                }}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-4">
                Alterar o papel de <strong>{selectedMember.name}</strong> no projeto:
              </p>
              
              <div className="space-y-3">
                {['Gerente de Projeto', 'Arquiteto', 'Desenvolvedor', 'Designer', 'Analista', 'Membro'].map(role => (
                  <button
                    key={role}
                    onClick={() => handleRoleChange(selectedMember, role)}
                    className={`w-full text-left p-4 rounded-md border-2 transition-colors ${
                      selectedMember.role === role
                        ? 'border-coral bg-coral-soft'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    disabled={isLoading}
                  >
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="font-medium">{role}</div>
                      </div>
                      {selectedMember.role === role && (
                        <CheckCircle className="w-5 h-5 text-coral ml-auto" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowRoleModal(false);
                  setSelectedMember(null);
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                disabled={isLoading}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectMembersManager; 